'use client'

import { useMemo, useRef, useState } from 'react'

// The chain as a graph: every stem a node, every "b followed a in some name"
// an edge, laid out left to right by where a stem usually sits in a name
// relative to the brand. A name with three or more stems is a walk through
// the middle ones, so names that share a middle stem visibly cross there —
// what the earlier brand-only before/after diagram could not show.
//
// Two modes over one layout: 'neighbourhood' (two hops around the selected
// stem, or around the brand) for the page, 'full' (every stem, pan/zoom) for
// the "see the whole chain" overlay.

export type ChainStem = { stem: string; count: number; is_head: boolean; is_lure: boolean }
export type ChainTransition = { a: string; b: string; count: number; sep: string; examples: string[] }
export type ChainRoute = { host: string; stems: string[] }

const ROW = 24
const COL_W = 168
const PAD = 28
const PER_COLUMN = 25

const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b)
  return s.length % 2 ? s[(s.length - 1) / 2] : Math.round((s[s.length / 2 - 1] + s[s.length / 2]) / 2)
}

/** Column per stem: its median position relative to the brand across the
 * names it appears in (brand = 0, before it negative); a brand-less name is
 * read as if the brand came first, so its stems start at +1 and only brand
 * stems sit in the brand column. Without routes, the hop distance from the
 * brand along the transitions. */
function columns(stems: ChainStem[], transitions: ChainTransition[], routes: ChainRoute[] | undefined, heads: Set<string>) {
  const col = new Map<string, number>()
  if (routes && routes.length > 0) {
    const pos = new Map<string, number[]>()
    for (const r of routes) {
      const h = r.stems.findIndex((s) => heads.has(s))
      r.stems.forEach((s, i) => { const p = h >= 0 ? i - h : i + 1; (pos.get(s) ?? pos.set(s, []).get(s)!).push(p) })
    }
    for (const [s, ps] of pos) col.set(s, heads.has(s) ? 0 : median(ps))
  } else {
    const out = new Map<string, string[]>(); const inn = new Map<string, string[]>()
    for (const t of transitions) { (out.get(t.a) ?? out.set(t.a, []).get(t.a)!).push(t.b); (inn.get(t.b) ?? inn.set(t.b, []).get(t.b)!).push(t.a) }
    const walk = (next: Map<string, string[]>, step: number) => {
      let frontier = [...heads]; let d = 0
      while (frontier.length) {
        d += step
        const nf: string[] = []
        for (const s of frontier) for (const n of next.get(s) ?? []) if (!col.has(n)) { col.set(n, d); nf.push(n) }
        frontier = nf
      }
    }
    for (const h of heads) col.set(h, 0)
    walk(out, 1); walk(inn, -1)
  }
  let far = 0
  for (const v of col.values()) far = Math.max(far, v)
  for (const s of stems) if (!col.has(s.stem)) col.set(s.stem, far + 1)
  return col
}

export function TokenChainGraph({
  stems, transitions, routes, heads, isDark, mode, minCount, selected, onSelect, highlight, onEdge, activeEdge, search,
}: {
  stems: ChainStem[]
  transitions: ChainTransition[]
  routes?: ChainRoute[]
  heads: string[]
  isDark: boolean
  mode: 'neighbourhood' | 'full'
  /** Links seen fewer times than this are hidden (and stems left without one). */
  minCount: number
  selected: string | null
  onSelect: (s: string | null) => void
  /** A walk to light up end to end: a host's route, a query's parts. */
  highlight?: string[] | null
  onEdge?: (t: ChainTransition) => void
  activeEdge?: ChainTransition | null
  /** Full mode: a stem to centre on. */
  search?: string
}) {
  const [hover, setHover] = useState<ChainTransition | null>(null)
  const headSet = useMemo(() => new Set(heads), [heads])
  const count = useMemo(() => new Map(stems.map((s) => [s.stem, s])), [stems])
  const col = useMemo(() => columns(stems, transitions, routes, headSet), [stems, transitions, routes, headSet])
  const lit = useMemo(() => new Set(highlight ?? []), [highlight])
  const litPairs = useMemo(() => {
    const p = new Set<string>()
    if (highlight) for (let i = 0; i + 1 < highlight.length; i++) p.add(`${highlight[i]}\u0000${highlight[i + 1]}`)
    return p
  }, [highlight])

  const layout = useMemo(() => {
    const edges = transitions.filter((t) => t.count >= minCount || litPairs.has(`${t.a}\u0000${t.b}`))
    let keep: Set<string>
    if (mode === 'full') {
      keep = new Set(stems.filter((s) => s.count >= minCount).map((s) => s.stem))
      for (const t of edges) { keep.add(t.a); keep.add(t.b) }
    } else {
      // two hops either way from the centre, over the links that pass minCount
      const centre = selected ? [selected] : heads
      const adj = new Map<string, string[]>()
      for (const t of edges) { (adj.get(t.a) ?? adj.set(t.a, []).get(t.a)!).push(t.b); (adj.get(t.b) ?? adj.set(t.b, []).get(t.b)!).push(t.a) }
      keep = new Set(centre)
      let frontier = [...centre]
      for (let hop = 0; hop < 2; hop++) {
        const nf: string[] = []
        for (const s of frontier) for (const n of adj.get(s) ?? []) if (!keep.has(n)) { keep.add(n); nf.push(n) }
        frontier = nf
      }
    }
    for (const s of lit) if (count.has(s)) keep.add(s)
    // per column, most-seen first; centre, selection and the lit walk always stay
    const byCol = new Map<number, string[]>()
    for (const s of keep) { const c = col.get(s) ?? 0; (byCol.get(c) ?? byCol.set(c, []).get(c)!).push(s) }
    const must = (s: string) => headSet.has(s) || s === selected || lit.has(s)
    let hidden = 0
    const cols = [...byCol.keys()].sort((a, b) => a - b)
    const pos = new Map<string, { x: number; y: number }>()
    let tallest = 0
    cols.forEach((c, ci) => {
      const list = byCol.get(c)!.sort((a, b) => Number(must(b)) - Number(must(a)) || (count.get(b)?.count ?? 0) - (count.get(a)?.count ?? 0) || a.localeCompare(b))
      const cap = mode === 'full' ? list.length : PER_COLUMN
      const shown = list.slice(0, Math.max(cap, list.filter(must).length))
      hidden += list.length - shown.length
      tallest = Math.max(tallest, shown.length)
      shown.forEach((s, i) => pos.set(s, { x: PAD + ci * COL_W, y: PAD + 14 + i * ROW }))
    })
    const drawn = edges.filter((t) => pos.has(t.a) && pos.has(t.b))
    return { pos, drawn, hidden, W: PAD * 2 + Math.max(cols.length - 1, 0) * COL_W + 120, H: PAD * 2 + 14 + tallest * ROW, colLabels: cols.map((c, ci) => ({ c, x: PAD + ci * COL_W })) }
  }, [transitions, minCount, litPairs, mode, stems, selected, heads, lit, count, col, headSet])

  // full mode: pan (drag) + zoom (wheel) through the viewBox
  const [view, setView] = useState<{ x: number; y: number; k: number }>({ x: 0, y: 0, k: 1 })
  const drag = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const searchHit = search ? [...layout.pos.keys()].find((s) => s.startsWith(search.trim().toLowerCase())) : undefined
  const vw = layout.W / view.k; const vh = Math.max(layout.H, 400) / view.k
  const vb = mode === 'full'
    ? (() => {
        if (searchHit && view.k === 1 && view.x === 0 && view.y === 0) { const p = layout.pos.get(searchHit)!; return `${p.x - 300} ${p.y - 200} 900 520` }
        return `${view.x} ${view.y} ${vw} ${vh}`
      })()
    : `0 0 ${layout.W} ${layout.H}`

  const ink = isDark ? '#e2e8f0' : '#1e293b'; const mutedInk = isDark ? '#64748b' : '#94a3b8'
  const hue = isDark ? '#38bdf8' : '#0284c7'; const litHue = '#f59e0b'
  const anyFocus = !!(hover || selected || (highlight && highlight.length) || activeEdge)
  // A link leaves from the end of its source's label, not the dot, so it
  // never runs through the text (monospace: ~0.6em a character).
  const labelEnd = (s: string) => {
    const info = count.get(s)
    const chars = s.length + 1 + String(info?.count ?? 0).length + (info?.is_lure ? 6 : 0)
    return 9 + chars * (headSet.has(s) ? 7.8 : 6.6) + 4
  }

  if (layout.pos.size === 0) return <div className="py-6 text-center font-mono text-[11px] text-slate-500">No stems pass this filter.</div>

  return (
    <div className={mode === 'full' ? 'relative h-full' : 'relative'}>
      <svg
        ref={svgRef}
        viewBox={vb}
        width="100%"
        style={mode === 'full' ? { height: '100%', cursor: drag.current ? 'grabbing' : 'grab' } : { minWidth: Math.min(layout.W, 900), maxHeight: 640 }}
        role="img"
        aria-label="Stems as nodes, the order they follow each other in phishing names as links"
        onWheel={mode === 'full' ? (e) => { e.preventDefault(); const k = Math.min(6, Math.max(0.3, view.k * (e.deltaY < 0 ? 1.15 : 1 / 1.15))); setView((v) => ({ ...v, k })) } : undefined}
        onPointerDown={mode === 'full' ? (e) => { drag.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y } } : undefined}
        onPointerMove={mode === 'full' ? (e) => {
          const d = drag.current; const el = svgRef.current
          if (!d || !el) return
          const scale = vw / el.clientWidth
          setView((v) => ({ ...v, x: d.vx - (e.clientX - d.x) * scale, y: d.vy - (e.clientY - d.y) * scale }))
        } : undefined}
        onPointerUp={() => { drag.current = null }}
        onPointerLeave={() => { drag.current = null }}
      >
        {layout.colLabels.map(({ c, x }) => (
          <text key={c} x={x} y={14} fontSize={9} fill={mutedInk} fontFamily="ui-monospace, monospace" letterSpacing="0.12em">
            {c === 0 ? 'BRAND' : c < 0 ? `${-c} BEFORE` : `+${c}`}
          </text>
        ))}
        {layout.drawn.map((t, i) => {
          const p1 = layout.pos.get(t.a)!; const p2 = layout.pos.get(t.b)!
          const onLit = litPairs.has(`${t.a}\u0000${t.b}`)
          const on = onLit || hover === t || activeEdge === t || selected === t.a || selected === t.b
          const dim = anyFocus && !on
          const x1 = p1.x + labelEnd(t.a)
          const d = p2.x > p1.x
            ? `M${x1},${p1.y} C${(x1 + p2.x) / 2},${p1.y} ${(x1 + p2.x) / 2},${p2.y} ${p2.x - 5},${p2.y}`
            // same column or backwards: an arc out to the right and back
            : `M${x1},${p1.y} C${x1 + 70},${p1.y + 30} ${p2.x + labelEnd(t.b) + 70},${p2.y + 30} ${p2.x + labelEnd(t.b)},${p2.y}`
          return (
            <g key={i} style={{ cursor: 'pointer' }} onMouseEnter={() => setHover(t)} onMouseLeave={() => setHover(null)} onClick={(e) => { e.stopPropagation(); onEdge?.(t) }}>
              <path d={d} fill="none" stroke={onLit ? litHue : hue} strokeOpacity={dim ? 0.08 : on ? 0.95 : 0.4} strokeWidth={Math.min(1 + t.count * 1.2, 14)} strokeLinecap="round" />
              <path d={d} fill="none" stroke="transparent" strokeWidth={12} />
            </g>
          )
        })}
        {[...layout.pos.entries()].map(([s, p]) => {
          const info = count.get(s); const head = headSet.has(s); const on = selected === s || lit.has(s) || s === searchHit
          const dim = anyFocus && !on && !(hover && (hover.a === s || hover.b === s)) && !(activeEdge && (activeEdge.a === s || activeEdge.b === s))
          return (
            <g key={s} style={{ cursor: 'pointer' }} opacity={dim ? 0.35 : 1} onClick={(e) => { e.stopPropagation(); onSelect(selected === s ? null : s) }}>
              <circle cx={p.x} cy={p.y} r={head ? 6 : 4} fill={head ? hue : lit.has(s) ? litHue : isDark ? '#0f161c' : '#ffffff'} stroke={lit.has(s) ? litHue : hue} strokeWidth={2} />
              <text x={p.x + 9} y={p.y + 3.5} fontFamily="ui-monospace, monospace" fontSize={head ? 13 : 11} fontWeight={head || on ? 700 : 400} fill={ink}>
                {s}<tspan fill={mutedInk} fontWeight={400}> {info?.count ?? 0}{info?.is_lure ? ' ·lure' : ''}</tspan>
              </text>
            </g>
          )
        })}
      </svg>
      {hover && (
        <div className={`pointer-events-none absolute left-1/2 top-2 -translate-x-1/2 rounded border px-2.5 py-1.5 font-mono text-[11px] shadow-lg ${isDark ? 'border-slate-700 bg-[#0b1116] text-slate-100' : 'border-slate-200 bg-white text-slate-800'}`}>
          <span className="font-semibold">{hover.a}</span>{hover.sep || '·'}<span className="font-semibold">{hover.b}</span>
          <span className="text-slate-500"> — in {hover.count} name(s) · click for every one</span>
        </div>
      )}
      {layout.hidden > 0 && (
        <div className="mt-1 font-mono text-[10px] text-slate-500">
          {layout.hidden} more stem(s) in these columns, least seen first to go — raise “seen at least” or open the whole chain
        </div>
      )}
    </div>
  )
}
